"use client";

import { ArrowRight, BookOpen } from "lucide-react";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import Link from "@/components/locale-link";
import { Button } from "@/components/ui/button";
import { useBibliographyState } from "@/components/use-bibliography-state";
import { t } from "@/lib/i18n/interpolate";
import { cn } from "@/lib/utils";

interface StudioCtaProps {
  /**
   * `bar` is the slim strip under every embedded generator; `card` is the standalone
   * block content pages use after their body copy.
   */
  variant?: "bar" | "card";
  className?: string;
}

/**
 * The one link that turns a one-off citation into a bibliography. Every generator
 * across the site saves into the same browser store the Studio reads, so this reads
 * the live count and says so — "3 saved in your Bibliography Studio" is a far better
 * reason to click than a feature blurb.
 */
export function StudioCta({ variant = "bar", className }: StudioCtaProps) {
  const bib = useBibStrings();
  const { citationCount } = useBibliographyState();
  const message =
    citationCount > 0
      ? t(bib.studioFooterSaved, { count: citationCount })
      : bib.studioFooterEmpty;

  if (variant === "card") {
    return (
      <div
        className={cn(
          "not-prose flex flex-col gap-4 rounded-xl border border-border bg-muted/30 p-5 sm:flex-row sm:items-center sm:justify-between",
          className
        )}
      >
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background shadow-soft">
            <BookOpen className="size-4" />
          </span>
          <div>
            <p className="font-semibold text-foreground">{bib.studioTitle}</p>
            <p className="mt-0.5 text-muted-foreground text-sm">{message}</p>
          </div>
        </div>
        <Button asChild className="shrink-0" size="sm">
          <Link href="/bibliography">
            {bib.studioOpen}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border/60 border-dashed bg-muted/20 px-3 py-2 text-sm",
        className
      )}
    >
      <BookOpen className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 text-muted-foreground">{message}</span>
      <Link
        className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline"
        href="/bibliography"
      >
        {bib.studioOpen}
        <ArrowRight className="size-3.5" />
      </Link>
    </div>
  );
}
